import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { InviteSection } from './LandingTemplate';
import { setCanonical } from '../lib/seo';

const SERIF = "'Cormorant Garamond', serif";
const CONSTELLATION =
  'https://customer-assets.emergentagent.com/job_dc9e3538-f079-41ff-9232-076517c20916/artifacts/ohrw8n76_image.png';
const CALENDLY = 'https://calendly.com/hello-archen';

const Eyebrow = ({ children, className = '' }) => (
  <p className={`text-[#7A5A2E] text-[11px] tracking-[0.24em] uppercase ${className}`}>{children}</p>
);

const GROUPS = [
  {
    eyebrow: "Your studio's memory",
    cards: [
      { cat: 'Project Memory', flag: true, title: 'Project Memory', desc: 'The living history of every project decision.', to: '/mockup/lp-007' },
      { cat: 'Decision Documentation', title: 'Decision Documentation', desc: "Every choice logged as it's made, not after.", to: '/mockup/lp-008' },
      { cat: 'Knowledge Management', title: 'Knowledge Management', desc: "Your firm's know-how, built automatically.", to: '/mockup/lp-009' },
    ],
  },
  {
    eyebrow: 'What your clients see',
    cards: [
      { cat: 'Client Approvals', title: 'Client Approvals', desc: 'Every sign-off, captured from the email it happened in.', to: '/mockup/lp-003' },
      { cat: 'Client Communication', title: 'Client Communication', desc: 'Client threads turned into a searchable record.', to: '/mockup/lp-004' },
      { cat: 'Client Portal', title: 'Client Portal', desc: 'A client view that updates itself.', to: '/mockup/lp-005' },
    ],
  },
];

const Card = ({ card }) => (
  <Link
    to={card.to}
    data-testid={`product-card-${card.to.split('/').pop()}`}
    className="group flex flex-col rounded-2xl border border-[#EAE0D2] bg-gradient-to-b from-[#FDFAF5] to-[#F7F0E6] p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-24px_rgba(60,45,30,0.35)]"
  >
    <div className="flex items-center gap-2.5 mb-4">
      <span className="text-[#7A5A2E] text-[9.5px] tracking-[0.16em] uppercase">{card.cat}</span>
      {card.flag && (
        <span className="text-[8.5px] tracking-[0.14em] uppercase text-[#42502F] bg-[#DCE5D2] rounded-full px-2 py-0.5">Flagship</span>
      )}
    </div>
    <h3
      className="text-[#1F1812] leading-[1.1]"
      style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 'clamp(1.5rem, 2vw, 1.85rem)' }}
    >
      {card.title}
    </h3>
    <p className="mt-3 text-[#3D3A36] text-[15px] leading-[1.6] flex-1">{card.desc}</p>
    <span className="mt-6 inline-flex items-center gap-1.5 text-[#7A5A2E] text-[13.5px] font-medium">
      Learn more
      <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
    </span>
  </Link>
);

const ProductHub = () => {
  useEffect(() => {
    document.title = 'The Product | Archen™ Project Memory';
    setCanonical('/product');
  }, []);

  return (
    <div className="bg-[#FDF8F3] min-h-screen antialiased" data-testid="product-hub">
      <Header />
      <main>
        {/* SECTION 1 — HERO (editorial, centered) */}
        <section className="pt-36 md:pt-44 pb-16 md:pb-20">
          <div className="content-container text-center">
            <Eyebrow className="mb-6">The Product</Eyebrow>
            <h1
              className="mx-auto text-[#1F1812] leading-[1.04] tracking-[-0.02em]"
              style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 'clamp(2.8rem, 5.6vw, 4.8rem)', maxWidth: '18ch' }}
              data-testid="product-hero-headline"
            >
              One record. Everything it powers.
            </h1>
            <p className="mt-7 mx-auto text-[#3D3A36] text-[18px] md:text-[20px] leading-[1.6] max-w-[62ch]">
              Archen™ turns the work your studio already does into a living project record, and everything that record becomes.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/foundingdesigner"
                data-testid="product-hero-primary"
                className="group inline-flex items-center gap-2 bg-[#1F1812] text-white text-[14px] font-medium px-7 py-3.5 rounded-lg transition-all duration-300 hover:bg-[#0F0D0A]"
              >
                Apply to join the Founding 50
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
              </a>
              <a
                href={CALENDLY}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-hero-secondary"
                className="text-[#3D3A36] hover:text-[#1F1812] text-[14px] font-medium underline underline-offset-4 decoration-[#C9B79A]"
              >
                Talk to the founders
              </a>
            </div>
          </div>
          <div className="mt-14 md:mt-16 content-container">
            <img
              src={CONSTELLATION}
              alt="Archen™ links vendors, materials, decisions, clients, lessons and specifications across every project into one record"
              data-testid="product-hero-graphic"
              className="w-full max-w-[1040px] mx-auto"
            />
          </div>
        </section>

        {/* SECTION 2 — FRAMING LINE */}
        <section className="bg-[#F5EFE6] py-20 md:py-28 border-y border-[#EAE1D3]/70">
          <div className="content-container text-center">
            <Eyebrow className="mb-6">How it fits together</Eyebrow>
            <p
              className="mx-auto text-[#1F1812] leading-[1.25]"
              style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 'clamp(1.7rem, 3vw, 2.6rem)', maxWidth: '26ch' }}
            >
              It starts with one record that builds itself. Everything below is what that record makes possible.
            </p>
          </div>
        </section>

        {/* SECTION 3 — TWO GROUPS, THREE CARDS EACH */}
        <section className="py-20 md:py-28">
          <div className="content-container flex flex-col gap-16 md:gap-20">
            {GROUPS.map((group) => (
              <div key={group.eyebrow}>
                <Eyebrow className="mb-8">{group.eyebrow}</Eyebrow>
                <div className="grid md:grid-cols-3 gap-6">
                  {group.cards.map((card) => (
                    <Card key={card.to} card={card} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* SECTION 4 — CONNECTIVE BAND */}
        <section className="bg-[#F5EFE6] py-20 md:py-28 border-y border-[#EAE1D3]/70">
          <div className="content-container max-w-[820px]">
            <Eyebrow className="mb-6">The thread</Eyebrow>
            <h2
              className="text-[#1F1812] leading-[1.12] tracking-[-0.02em]"
              style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 'clamp(2rem, 3.6vw, 3rem)' }}
            >
              Six capabilities. One record underneath.
            </h2>
            <p className="mt-7 text-[#3D3A36] text-[17px] md:text-[18px] leading-[1.7] max-w-[64ch]">
              The record is built automatically from the communication your team already produces, so every capability stays current with zero data entry.
            </p>
          </div>
        </section>

        {/* SECTION 5 — CLOSER (green Founding 50 invitation, consistent site-wide) */}
        <InviteSection s={{
          eyebrow: 'Founding 50',
          h2: 'You are invited to help shape what comes next.',
          h2Max: '30ch',
          blocks: [
            { text: 'Fifty founding firms. A direct line to the team, a real say in the roadmap, and the first look at everything new, the self-updating client portal included.' },
            { text: 'Fifty seats. Applications are open now.' },
          ],
          footnote: '50 founding firms · Direct access to the team · A voice in what we build next',
        }} />
      </main>
      <Footer />
    </div>
  );
};

export default ProductHub;
